import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import cx from 'classnames'
import * as React from 'react'

import styles from './ProductDetailView.module.scss'
import BlueConicContent from '../../../BlueConicContent'
import { Responsive } from '../../../core/Responsive'
import { useCoreConfigValue } from '../../../coreConfig.query'
import ErrorBoundary from '../../../ErrorBoundary'
import Container from '../../../layout/Container'
import TextWithMedia from '../../../TextWithMedia'
import makeMediaUrl, { MediaType } from '../../../utils/makeMediaUrl'
import { CheckImage } from '../../../utils/ProductContentImage'
import RecommendedProducts from '../../RecommendedProducts'
import FlavourImage from '../FlavourImage'
import { GroupedProduct, isGroupedProduct, Product } from '../GetProduct.query'
import LayoutTop from '../LayoutTop'

interface Props {
    product?: Product | GroupedProduct
    className?: string
    hideStickyCta: (value: boolean) => void
}

const mappedContent = (product: Product) => ({
    contentAreaTop: {
        hasContent: Boolean(
            product.contentMiddleTitle &&
                CheckImage(product.contentMiddleImageTwo),
        ),
        title: product.contentMiddleTitle,
        content: product.contentMiddleContent,
        image: product.contentMiddleImageTwo,
    },
    contentAreaBottom: {
        hasContent: Boolean(
            product.contentBottomTitle &&
                CheckImage(product.contentBottomImageTwo),
        ),
        title: product.contentBottomTitle,
        content: product.contentBottomContent,
        image: product.contentBottomImageTwo,
    },
    fullWidthFirst: {
        image: product.contentSizeGuideImageAbove,
    },
})

const ProductDetailView = ({ product, className, hideStickyCta }: Props) => {
    const { value: upsell_enabled } = useCoreConfigValue(
        'tweakwise/recommendations/upsell_enabled',
    )

    const { value: featured_enabled } = useCoreConfigValue(
        'tweakwise/recommendations/featured_enabled',
    )

    const isUpsellEnabled = upsell_enabled === '1'
    const isFeaturedEnabled = featured_enabled === '1'
    const content =
        product && !isGroupedProduct(product) && mappedContent(product)

    return (
        <div className={cx(styles.default, className)} data-testid="product">
            <Container>
                <LayoutTop product={product} hideStickyCta={hideStickyCta} />
            </Container>

            <div className={styles.contentWrapper}>
                {isUpsellEnabled && product && (
                    <ErrorBoundary fallback={<React.Fragment />}>
                        <Container>
                            <BlueConicContent blueConicPosition="pdpSlider">
                                <RecommendedProducts
                                    header={
                                        isGroupedProduct(product)
                                            ? t({
                                                  id: 'catalog.productDetailView.looksUpSellTitle',
                                                  message: `More looks for you`,
                                              })
                                            : t({
                                                  id: 'catalog.productDetailView.upSellTitle',
                                                  message: `You might also like`,
                                              })
                                    }
                                    productId={product.id}
                                    withSizeSelect
                                    recommendationsType="upsell"
                                    overrule={false}
                                    position="pdp"
                                    itemListSlot="pdpSlider"
                                />
                            </BlueConicContent>
                        </Container>
                    </ErrorBoundary>
                )}
                {content &&
                    content.contentAreaTop.hasContent &&
                    content.contentAreaTop.image && (
                        <TextWithMedia
                            alignment="center"
                            // eslint-disable-next-line react/style-prop-object
                            style="light"
                            heading={content.contentAreaTop.title || ''}
                            content={
                                content.contentAreaTop.content ?? undefined
                            }
                            images={[
                                {
                                    url: makeMediaUrl(
                                        content.contentAreaTop.image,
                                        MediaType.Product,
                                    ),
                                    alt: '',
                                },
                            ]}
                            withOffset={false}
                            mobileMediaPosition="bottom"
                            headingFuss={['castiron', 'pme', 'vanguard']}
                        />
                    )}

                {content &&
                    content.fullWidthFirst.image &&
                    CheckImage(content.fullWidthFirst.image) && (
                        <Responsive md up>
                            <FlavourImage
                                image={content.fullWidthFirst.image}
                            />
                        </Responsive>
                    )}

                {isFeaturedEnabled && product && (
                    <ErrorBoundary fallback={<React.Fragment />}>
                        <Container>
                            <RecommendedProducts
                                withSizeSelect
                                header={t({
                                    id: 'catalog.productDetailView.crossSellTitle',
                                    message: `Complete your look`,
                                })}
                                productId={product.id}
                                recommendationsType="featured"
                                overrule
                                position="pdp"
                                itemListSlot="pdpCompleteYourLook"
                            />
                        </Container>
                    </ErrorBoundary>
                )}

                {content &&
                    content.contentAreaBottom.hasContent &&
                    content.contentAreaBottom.image && (
                        <TextWithMedia
                            // eslint-disable-next-line react/style-prop-object
                            style="light"
                            heading={content.contentAreaBottom.title || ''}
                            content={
                                content.contentAreaBottom.content ?? undefined
                            }
                            images={[
                                {
                                    url: makeMediaUrl(
                                        content.contentAreaBottom.image,
                                        MediaType.Product,
                                    ),
                                    alt: '',
                                },
                            ]}
                            desktopMediaPosition="left"
                            mobileMediaPosition="bottom"
                            alignment="center"
                            withOffset={false}
                            headingFuss={['castiron', 'pme', 'vanguard']}
                        />
                    )}
            </div>
        </div>
    )
}

export default ProductDetailView
